import { useState } from "react";
import { toast } from "sonner";
import { Copy, Eye, HardDrives } from "@phosphor-icons/react";
import api, { apiErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const when = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { timeZone: "America/New_York", weekday: "long", hour: "numeric", minute: "2-digit" }) : "—");
const TONE = { success: "text-emerald-700 bg-emerald-50", partial: "text-amber-700 bg-amber-50", failed: "text-rose-700 bg-rose-50", running: "text-slate-600 bg-slate-100" };

export function SyncHistory({ rows }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200" data-testid="wa-sync-history">
      <div className="px-4 py-3 border-b border-slate-100 text-sm font-semibold text-slate-900">Sync History</div>
      {!rows.length ? <div className="px-4 py-5 text-xs text-slate-500" data-testid="wa-sync-history-empty">No sync attempts yet.</div> : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead><tr className="text-left text-[10px] uppercase tracking-widest text-slate-400"><th className="px-4 py-2">Date / Time</th><th className="px-2">Status</th><th className="px-2">New Messages</th><th className="px-2">Trigger</th><th className="px-2 pr-4">Details</th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-slate-100 align-top" data-testid={`wa-sync-row-${r.id}`}>
                  <td className="px-4 py-2 whitespace-nowrap text-slate-700">{when(r.started_at)}</td>
                  <td className="px-2 py-2"><span className={`px-2 py-0.5 rounded-full font-semibold capitalize ${TONE[r.status] || ""}`}>{r.status}</span></td>
                  <td className="px-2 py-2 tabular-nums font-semibold text-slate-800">{r.new_messages ?? 0}</td>
                  <td className="px-2 py-2 text-slate-500">{r.trigger}</td>
                  <td className="px-2 pr-4 py-2 text-slate-600">
                    {r.details || (r.status === "running" ? "In progress…" : "")}
                    {(r.gaps || []).map((g, i) => (
                      <div key={i} className="text-amber-700 mt-0.5" data-testid="wa-sync-gap">Possible gap · {g.group_name || g.group_id}: {when(g.from)} → {when(g.to)} — {g.reason}</div>
                    ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function WorkerCard({ sync, canLaunch }) {
  const { user } = useAuth();
  const [token, setToken] = useState("");
  const reveal = async () => { try { const { data } = await api.get("/dailylogs/worker-setup"); setToken(data.token); } catch (e) { toast.error(apiErrorMessage(e, "Could not load token")); } };
  const copy = (v) => navigator.clipboard.writeText(v).then(() => toast.success("Copied"));
  const row = (k, v, id) => (
    <div className="flex items-center gap-2 font-mono text-[11px] bg-slate-50 border border-slate-200 rounded px-2 py-1" data-testid={id}>
      <span className="text-slate-500">{k}=</span><span className="truncate flex-1 text-slate-800">{v}</span>
      <button onClick={() => copy(v)} className="text-slate-400 hover:text-[#327de3]" data-testid={`${id}-copy`}><Copy size={12} /></button>
    </div>
  );
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-2" data-testid="wa-worker-card">
      <div className="flex items-center gap-2 text-sm font-semibold text-slate-900"><HardDrives size={16} className="text-[#327de3]" /> WhatsApp worker</div>
      <div className="text-xs text-slate-600" data-testid="wa-worker-health">
        <b className={sync?.worker_online ? "text-emerald-700" : "text-rose-600"}>{sync?.worker_online ? "Online" : "Offline"}</b>
        {sync?.worker_host ? ` · ${sync.worker_mode === "local" ? "running on this Strata server" : `server ${sync.worker_host}`}` : ""}
        {` · last heartbeat ${when(sync?.heartbeat_at)}`}
      </div>
      {!canLaunch && (
        <div className="text-xs text-slate-600 space-y-2" data-testid="wa-remote-setup">
          <p>This Strata server can&apos;t run the WhatsApp browser itself. Run the worker on your own always-on server (Docker, 1&nbsp;GB RAM, a volume at <code>/data</code>). The steps are in <code>wa_worker/README.md</code>. Use these settings:</p>
          {row("WA_BACKEND_URL", window.location.origin, "wa-setup-url")}
          {row("WA_WORKER_MODE", "remote", "wa-setup-mode")}
          {user?.is_super_admin && (token ? row("WA_INTERNAL_TOKEN", token, "wa-setup-token") : (
            <button onClick={reveal} className="inline-flex items-center gap-1 text-[#327de3] font-semibold hover:underline" data-testid="wa-setup-reveal-token"><Eye size={12} /> Show WA_INTERNAL_TOKEN</button>
          ))}
        </div>
      )}
    </div>
  );
}
